import React, { useState, useEffect } from 'react';
import { Plus, Minus, User, Wallet, Loader, CreditCard, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useOutletContext } from 'react-router-dom';
import { useCart } from '../../Api/CartContext';
import { BASE_URL } from '../../Api/Config';

function Cart() {
  // Get login functions from PublicLayout
  const outletContext = useOutletContext() || {};
  const { onLoginClick, onSignUpClick } = outletContext;

  const {
    cart,
    loading: cartLoading,
    error: cartError,
    updateQuantity,
    removeFromCart,
    clearCart,
    clearError,
    loadCart
  } = useCart();

  const [user, setUser] = useState(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Payment states
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    phone: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setCustomerDetails({ phone: '' });
      setShowPaymentForm(false);
    };

    // Listen for the logout event that the header dispatches
    window.addEventListener('logout', handleLogout);

    return () => {
      window.removeEventListener('logout', handleLogout);
    };
  }, []);


  useEffect(() => {
    const handleStorageChange = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setCustomerDetails({
          phone: parsedUser.phone || '',
        });
      } else {
        setUser(null);
      }
    };

    // Listen for storage changes (when user logs in from header)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for our custom login event
    window.addEventListener('userLoggedIn', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleStorageChange);
    };
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        // Pre-fill customer details from user data
        setCustomerDetails({
          phone: parsedUser.phone || '',
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Handle initial loading and ensure cart is loaded
  useEffect(() => {
    const initializeCart = async () => {
      try {
        await loadCart();

      } catch (error) {
        console.error('Error loading cart:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    initializeCart();
    // eslint-disable-next-line
  }, []);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Updated login handlers to use PublicLayout's functions
  const handleLoginClick = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const handleSignUpClick = () => {
    if (onSignUpClick) {
      onSignUpClick();
    }
  };

  const handleUpdateQuantity = async (foodId, newQuantity) => {
    try {
      if (newQuantity < 1) {
        await removeFromCart(foodId);
      } else {
        await updateQuantity(foodId, newQuantity);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // const handleRemoveItem = async (foodId) => {
  //   try {
  //     await removeFromCart(foodId);
  //   } catch (error) {
  //     console.error('Error removing item:', error);
  //   }
  // };

  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Validate customer details
  const validateForm = () => {
    const newErrors = {};

    if (!customerDetails.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(customerDetails.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle payment failure
  const handlePaymentFailure = async (failureData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/payment/failure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(failureData)
      });

      if (!response.ok) {
        throw new Error('Failed to record payment failure');
      }

      alert(`Payment failed: ${failureData.error?.description || 'Payment was cancelled or failed'}`);
    } catch (error) {
      console.error('Error handling payment failure:', error);
      alert('Payment failed. Please try again.');
    }
  };

  // Handle Place Order with Razorpay Payment
  const handlePlaceOrder = async () => {
    if (!user) {
      alert('Please log in to place an order');
      return;
    }

    if (!cartItems.length) {
      alert('Your cart is empty');
      return;
    }

    // Show payment form if details are missing
    if (!customerDetails.phone) {
      setShowPaymentForm(true);
      return;
    }

    if (!validateForm()) {
      setShowPaymentForm(true);
      return;
    }

    setIsProcessingOrder(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Failed to load payment gateway. Please try again.');
        setIsProcessingOrder(false);
        return;
      }

      const token = localStorage.getItem('token');

      // Create payment order - Fixed API endpoint URL
      const orderResponse = await fetch(`${BASE_URL}/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerPhone: customerDetails.phone
        })
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.message || 'Failed to create payment order');
      }

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        alert(orderData.message || 'Failed to create payment order');
        setIsProcessingOrder(false);
        return;
      }

      // Razorpay options
      const options = {
        key: orderData.data.key,
        amount: orderData.data.amount,
        currency: orderData.data.currency,
        name: orderData.data.name,
        description: orderData.data.description,
        order_id: orderData.data.orderId,
        prefill: orderData.data.prefill,
        theme: orderData.data.theme,
        method: {
          upi: true,
          card: true,
          netbanking: false,
          wallet: false
        },
        handler: async function (response) {
          // Payment successful - verify payment
          try {
            setIsProcessingOrder(true);

            const verifyResponse = await fetch(`${BASE_URL}/payment/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                paymentId: orderData.data.paymentId
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              alert('Payment successful! Your order has been placed.');
              await loadCart(); // Refresh cart (should be empty now)
              setShowPaymentForm(false);
              // Optionally redirect to orders page
              // window.location.href = '/orders';
            } else {
              throw new Error(verifyData.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed. Please contact support.');
          } finally {
            setIsProcessingOrder(false);
          }
        },
        modal: {
          ondismiss: function () {
            // Payment cancelled or dismissed
            handlePaymentFailure({
              error: { description: 'Payment cancelled by user' },
              paymentId: orderData.data.paymentId
            });
            setIsProcessingOrder(false);
          }
        }
      };

      // Create and open Razorpay checkout
      const razorpay = new window.Razorpay(options);

      // Handle payment failure
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        handlePaymentFailure({
          error: response.error,
          paymentId: orderData.data.paymentId
        });
        setIsProcessingOrder(false);
      });

      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || 'Failed to initiate payment. Please try again.');
      setIsProcessingOrder(false);
    }
  };

  const cartItems = cart?.items || [];
  const cartItemCount = cart?.itemCount || 0;
  const cartTotalAmount = cart?.totalAmount || 0;

  // Update all references to use these safe variables
  const subtotal = cartTotalAmount;
  const serviceFee = 0.00;
  const total = subtotal + serviceFee;

  if (initialLoading || (cartLoading && !cartItems.length && !cartError)) {
    return (
      <section className="py-16 px-4 bg-snowbg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-redtxt mx-auto mb-4" />
          <p className="text-lightGray">Loading cart...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-snowbg min-h-screen">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 className="text-lightGray text-sm md:text-base max-w-full mx-auto text-left pb-2"><Link to='/'>Home</Link> / Cart</h3>
          <h2 className="text-redtxt text-2xl md:text-4xl font-bold mb-4 text-left">Your Food Items</h2>
        </motion.div>

        {/* Error Display */}
        {cartError && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-red-600 text-sm">{cartError}</p>
            <button
              onClick={clearError}
              className="text-red-600 text-xs underline mt-1"
            >
              Dismiss
            </button>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="order-2 lg:order-1 lg:col-span-2 space-y-4">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-snowbg rounded-xl overflow-visible group flex flex-col gap-4">

              {/* Account Section */}
              {!user ? (
                <div className="relative flex flex-col p-4 pl-10 border-b bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <User className="w-10 h-10 bg-darkbg text-white p-2 absolute -left-5 top-1/4 hidden lg:block" />
                  <h3 className="text-xl md:text-2xl font-bold mb-1">Account</h3>
                  <p className="text-darktxt text-sm md:text-base mb-2">To place your order now, log in to your existing account or sign up.</p>
                  <div className="py-4 flex gap-2">
                    <button
                      onClick={handleLoginClick}
                      className="bg-white text-redtxt border border-white text-sm md:text-base font-semibold px-8 py-3 rounded-sm shadow-md hover:shadow-lg transition-all"
                    >
                      Login
                    </button>
                    <button
                      onClick={handleSignUpClick}
                      className="bg-redtxt text-white border border-redtxt text-sm md:text-base font-semibold px-8 py-3 rounded-sm shadow-md hover:shadow-lg transition-all hover:bg-red-600"
                    >
                      Sign Up
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative flex flex-col p-4 pl-10 border-b bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <User className="w-10 h-10 bg-darkbg text-white p-2 absolute -left-5 top-1/4 hidden lg:block" />
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Account</h3>
                  <p className="text-darktxt text-sm md:text-base mb-1">
                    {user.name || user.email}
                  </p>
                  <p className="text-darktxt text-sm md:text-base mb-2">
                    {user.phone || 'Phone not provided'}
                  </p>
                </div>
              )}

              {/* Payment Details Form */}
              {user && cartItems.length > 0 && showPaymentForm && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="relative flex flex-col p-4 pl-10 border-b bg-white rounded-lg shadow-lg"
                >
                  <Phone className="w-10 h-10 bg-darkbg text-white p-2 absolute -left-5 top-1/4" />
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Delivery Details</h3>

                  <div className="space-y-4">
                    {/* Phone Number */}
                    <div>
                      <label className="text-darktxt text-sm md:text-base mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={customerDetails.phone}
                        onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-redtxt ${errors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter your phone number"
                        maxLength="10"
                      />
                      {errors.phone && (
                        <p className="text-redtxt text-sm mt-2">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="py-4 flex gap-2">
                    <button
                      onClick={() => setShowPaymentForm(false)}
                      className="bg-white text-redtxt border border-white text-sm md:text-base font-semibold px-8 py-3 rounded-sm shadow-md hover:shadow-lg transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={isProcessingOrder}
                      className="bg-redtxt text-white border border-redtxt text-sm md:text-base font-semibold px-8 py-3 rounded-sm shadow-md hover:shadow-lg transition-all hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isProcessingOrder ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Proceed to Pay
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Payment Section - Only show if user is logged in and cart is not empty */}
              {user && cartItems.length > 0 && !showPaymentForm && (
                <div className="relative flex flex-col p-4 pl-10 border-b bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <Wallet className="w-10 h-10 bg-darkbg text-white p-2 absolute -left-5 top-1/4 hidden lg:block" />
                  <h3 className="ext-xl md:text-2xl font-bold mb-2">Payment</h3>
                  <p className="text-darktxt text-sm md:text-base mb-2">
                    Secure online payment via Razorpay (Test Mode)
                  </p>
                  <div className="py-4">
                    <button
                      onClick={handlePlaceOrder}
                      disabled={isProcessingOrder || cartLoading}
                      className="flex items-center gap-2 bg-redtxt text-white border border-redtxt text-sm md:text-base font-semibold px-8 py-3 rounded-sm shadow-md hover:shadow-lg transition-all hover:bg-red-600"
                    >
                      {isProcessingOrder ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Place Order
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-1">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-snowbg rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">

                {/* Cart Items */}
                <div className="space-y-3 pb-4 border-b border-gray-400">
                  {cartItems.length === 0 ? (
                    <div className="space-y-3 p-4 text-center text-lightGrey text-sm md:text-base bg-white">
                      Your cart is empty, <Link to='/restaurants' className='text-blue-500 underline'>Order Now</Link>
                    </div>
                  ) : (
                    <>
                      {/* Restaurant Info */}
                      {cart?.restaurant && (
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-16 h-16 border rounded-lg flex-shrink-0 overflow-hidden bg-gray-100">
                            {cart.restaurant.image ? (
                              <img
                                src={cart.restaurant.image}
                                alt={cart.restaurant.name}
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-lightGray">
                                <span className="text-xs">Restaurant</span>
                              </div>
                            )}
                          </div>
                          <div className='flex flex-col'>
                            <span className="text-darktxt text-sm md:text-base font-semibold">
                              <Link to={`/restaurantMenu/${cart.restaurant._id}`}>{cart.restaurant.name}</Link>
                            </span>
                            <span className="text-darktxt text-sm md:text-base font-semibold">
                              {cart.restaurant.address}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Cart Items List */}
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {cartItems.map(item => (
                          <div
                            key={item.food._id}
                            className="flex flex-wrap items-center justify-between gap-3 p-2">
                            <p className="flex-1 min-w-[120px] text-darktxt text-sm md:text-base">
                              {item.food.name}
                            </p>
                            <div className="flex items-center border border-gray-300 px-1 shrink-0">
                              <button
                                onClick={() => handleUpdateQuantity(item.food._id, item.quantity - 1)}
                                disabled={cartLoading}
                                className="w-7 h-7 flex items-center justify-center disabled:opacity-50">
                                <Minus className="w-4 h-4 text-redtxt" />
                              </button>
                              <span className="w-8 h-7 flex items-center justify-center font-medium text-redtxt text-sm">
                                {cartLoading ? '...' : item.quantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(item.food._id, item.quantity + 1)}
                                disabled={cartLoading}
                                className="w-7 h-7 flex items-center justify-center disabled:opacity-50">
                                <Plus className="w-4 h-4 text-redtxt" />
                              </button>
                            </div>
                            <p className="w-12 text-darktxt text-sm md:text-base shrink-0 text-right">
                              ₹{(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Clear Cart Button */}
                      {cartItems.length > 0 && (
                        <div className="flex justify-end mb-3">
                          <button
                            onClick={handleClearCart}
                            disabled={cartLoading}
                            className="text-redtxt text-sm md:text-base underline disabled:opacity-50"
                          >
                            Clear Cart
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Order Summary */}
                <div className="space-y-3">
                  <h3 className="text-darktxt text-base md:text-lg font-semibold mb-2">Order Summary</h3>
                  <div className="flex justify-between text-lightGray text-sm md:text-base">
                    <p>Subtotal ({cartItemCount} items)</p>
                    <p className="font-semibold">₹{subtotal.toFixed(2)}</p>
                  </div>
                  <div className="flex justify-between text-lightGray text-sm md:text-base">
                    <p>Service fee</p>
                    <p className="font-semibold">₹{serviceFee.toFixed(2)}</p>
                  </div>
                  <div className="border-t border-gray-300 pt-3">
                    <div className="flex justify-between text-darktxt text-base md:text-lg font-semibold">
                      <p>Total</p>
                      <p>₹{total.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Payment Security Note */}
                  {/* {user && cartItems.length > 0 && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-800 text-sm">
                        <CreditCard className="w-4 h-4" />
                        <span>Secure payment powered by Razorpay (Test Mode)</span>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Cart;